import { StatisticalMetrics, EnhancedPattern } from '@/types/trading-analysis-types';

export class StatisticalValidator {
  /**
   * Calculate chi-square test for pattern significance
   */
  static calculateChiSquare(
    observed: { wins: number; losses: number },
    expected: { wins: number; losses: number }
  ): { chiSquare: number; pValue: number } {
    const totalObserved = observed.wins + observed.losses;
    const totalExpected = expected.wins + expected.losses;
    
    if (totalObserved === 0 || totalExpected === 0) {
      return { chiSquare: 0, pValue: 1 };
    }

    // Chi-square calculation
    const chiSquareWins = Math.pow(observed.wins - expected.wins, 2) / expected.wins;
    const chiSquareLosses = Math.pow(observed.losses - expected.losses, 2) / expected.losses;
    const chiSquare = chiSquareWins + chiSquareLosses;

    // Approximate p-value calculation (df = 1)
    const pValue = this.chiSquarePValue(chiSquare, 1);

    return { chiSquare, pValue };
  }

  /**
   * Approximate chi-square p-value calculation
   */
  private static chiSquarePValue(chiSquare: number, degreesOfFreedom: number): number {
    // Simplified approximation for df = 1
    if (degreesOfFreedom === 1) {
      if (chiSquare >= 10.83) return 0.001;
      if (chiSquare >= 6.63) return 0.01;
      if (chiSquare >= 3.84) return 0.05;
      if (chiSquare >= 2.71) return 0.1;
      return 0.5;
    }
    
    // For other df values, return a rough approximation
    return Math.max(0.001, Math.exp(-chiSquare / 2));
  }

  /**
   * Calculate confidence interval for success rate
   */
  static calculateConfidenceInterval(
    successRate: number,
    sampleSize: number,
    confidenceLevel: number = 0.95
  ): [number, number] {
    if (sampleSize <= 0) return [0, 0];

    const p = successRate / 100;
    const n = sampleSize;
    
    // Z-score for confidence level
    const zScore = confidenceLevel === 0.95 ? 1.96 : 
                   confidenceLevel === 0.99 ? 2.576 : 
                   confidenceLevel === 0.90 ? 1.645 : 1.96;

    // Standard error
    const standardError = Math.sqrt((p * (1 - p)) / n);
    
    // Margin of error
    const marginOfError = zScore * standardError;
    
    // Confidence interval
    const lowerBound = Math.max(0, (p - marginOfError) * 100);
    const upperBound = Math.min(100, (p + marginOfError) * 100);
    
    return [
      Math.round(lowerBound * 100) / 100,
      Math.round(upperBound * 100) / 100
    ];
  }

  /**
   * Determine statistical significance level
   */
  static getSignificanceLevel(pValue: number): 'low' | 'medium' | 'high' | 'very_high' {
    if (pValue <= 0.001) return 'very_high';
    if (pValue <= 0.01) return 'high';
    if (pValue <= 0.05) return 'medium';
    return 'low';
  }

  /**
   * Validate pattern statistical significance
   */
  static validatePattern(
    pattern: EnhancedPattern,
    overallWinRate: number,
    confidenceLevel: number = 0.95
  ): StatisticalMetrics {
    const sampleSize = pattern.nextWinCount + pattern.nextLossCount;
    
    if (sampleSize === 0) {
      return {
        sampleSize: 0,
        significance: 'low'
      };
    }

    // Expected outcomes based on overall win rate
    const expectedWins = sampleSize * (overallWinRate / 100);
    const expectedLosses = sampleSize * ((100 - overallWinRate) / 100);

    // Chi-square test
    const { chiSquare, pValue } = this.calculateChiSquare(
      { wins: pattern.nextWinCount, losses: pattern.nextLossCount },
      { wins: expectedWins, losses: expectedLosses }
    );

    // Confidence interval
    const confidenceInterval = this.calculateConfidenceInterval(
      pattern.successRate,
      sampleSize,
      confidenceLevel
    );

    // Significance level
    const significance = this.getSignificanceLevel(pValue);

    return {
      chiSquare: Math.round(chiSquare * 1000) / 1000,
      pValue: Math.round(pValue * 1000) / 1000,
      confidenceInterval,
      significance,
      sampleSize
    };
  }

  /**
   * Filter patterns by statistical significance
   */
  static filterSignificantPatterns(
    patterns: EnhancedPattern[],
    overallWinRate: number,
    minSignificance: 'low' | 'medium' | 'high' | 'very_high' = 'medium'
  ): EnhancedPattern[] {
    const significanceOrder = ['low', 'medium', 'high', 'very_high'];
    const minLevel = significanceOrder.indexOf(minSignificance);

    return patterns.filter(pattern => {
      if (!pattern.statisticalMetrics) return false;
      
      const patternLevel = significanceOrder.indexOf(pattern.statisticalMetrics.significance || 'low');
      return patternLevel >= minLevel;
    });
  }

  /**
   * Calculate overall statistical confidence for a set of patterns
   */
  static calculateOverallConfidence(patterns: EnhancedPattern[]): number {
    if (patterns.length === 0) return 0;

    const significantPatterns = patterns.filter(p => 
      p.statisticalMetrics && p.statisticalMetrics.significance && 
      ['medium', 'high', 'very_high'].includes(p.statisticalMetrics.significance)
    );

    const totalPatterns = patterns.length;
    const significantRatio = significantPatterns.length / totalPatterns;

    // Weight by sample sizes
    const totalSampleSize = patterns.reduce((sum, p) => 
      sum + (p.statisticalMetrics?.sampleSize || 0), 0);
    
    const weightedSignificance = significantPatterns.reduce((sum, p) => {
      const weight = (p.statisticalMetrics?.sampleSize || 0) / totalSampleSize;
      const signifcanceValue = p.statisticalMetrics?.significance === 'very_high' ? 4 :
                              p.statisticalMetrics?.significance === 'high' ? 3 :
                              p.statisticalMetrics?.significance === 'medium' ? 2 : 1;
      return sum + (weight * signifcanceValue);
    }, 0);

    return Math.min(100, Math.round(significantRatio * weightedSignificance * 25));
  }
}